import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Textarea,
} from '@roger/ui-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AlertCircle,
  Check,
  Info,
  Loader2,
  MessageSquarePlus,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import {
  getQuestionCustomizations,
  getQuestionUsage,
  orderQuestionUsageSources,
  saveQuestionCustomizations,
  type QuestionCustomizationsQuestion,
  type QuestionCustomizationsResponse,
  type QuestionUsage,
  type QuestionUsageSource,
} from '../../services/questionCustomizations';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { groupQuestionCatalog } from './questionCatalogGrouping';
import { useIsMobile, useMediaQuery } from '../../hooks/useIsMobile';
import { questionDisplayLabels } from './questionDisplayLabel';

const MAX_ADDITIONS = 30;
const QUESTION_ROW_HEIGHT = 44;
const QUESTION_LIST_HEIGHT = 'h-56';
const MAX_GUIDANCE_ENTRIES = 15;
const MAX_GUIDANCE_LENGTH = 2000;
const BLANK_GUIDANCE_MESSAGE =
  'Every guidance entry needs instructions. Fill one in or remove it.';

/** One question the agency has written its own answering instructions for. */
interface GuidanceEntry {
  qid: string;
  text: string;
}

type VirtualizedQuestionListProps = {
  listId: string;
  label: string;
  questions: QuestionCustomizationsQuestion[];
  renderQuestion: (question: QuestionCustomizationsQuestion) => React.ReactNode;
};

function VirtualizedQuestionList({
  listId,
  label,
  questions,
  renderQuestion,
}: VirtualizedQuestionListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: questions.length,
    getScrollElement: () => parentRef.current,
    // Every row is deliberately one line. Keeping the virtualizer's item size
    // fixed prevents long popover content from changing row placement.
    estimateSize: () => QUESTION_ROW_HEIGHT,
    getItemKey: index => questions[index]?.qid ?? index,
    overscan: 5,
  });

  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;
  useEffect(() => {
    virtualizerRef.current.scrollToOffset(0);
  }, [questions]);

  return (
    <div
      ref={parentRef}
      aria-label={label}
      className={`${QUESTION_LIST_HEIGHT} min-h-0 overflow-y-auto overscroll-contain rounded-md border`}
      data-testid={`question-list-${listId}`}
      role='list'
      tabIndex={0}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualizer.getVirtualItems().map(virtualItem => {
          const question = questions[virtualItem.index];
          if (!question) return null;
          return (
            <div
              key={virtualItem.key}
              role='listitem'
              data-index={virtualItem.index}
              style={{
                height: `${QUESTION_ROW_HEIGHT}px`,
                left: 0,
                position: 'absolute',
                top: 0,
                transform: `translateY(${virtualItem.start}px)`,
                width: '100%',
              }}
              className='box-border px-2 py-1'
            >
              {renderQuestion(question)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestionWording({
  question,
  sources = [],
}: {
  question: QuestionCustomizationsQuestion;
  sources?: readonly QuestionUsageSource[];
}) {
  const wording = question.question || question.description;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const clickOpenedRef = useRef(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const isMobile = useIsMobile();
  const isTouchPrimary = useMediaQuery('(pointer: coarse) and (hover: none)');
  const useDialogDisclosure = isMobile || isTouchPrimary;
  const cancelScheduledClose = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      if (
        !clickOpenedRef.current &&
        document.activeElement !== triggerRef.current
      ) {
        setIsOpen(false);
      }
    }, 100);
  };
  useEffect(() => () => cancelScheduledClose(), []);

  const trigger = (
    <Button
      ref={triggerRef}
      type='button'
      size='icon'
      variant='ghost'
      aria-label={`Show full question wording for ${question.description}`}
      className='shrink-0'
      onFocus={() => {
        if (useDialogDisclosure) return;
        cancelScheduledClose();
        setIsOpen(true);
      }}
      onBlur={() => {
        if (!useDialogDisclosure && !clickOpenedRef.current) scheduleClose();
      }}
      onPointerEnter={() => {
        if (useDialogDisclosure) return;
        cancelScheduledClose();
        setIsOpen(true);
      }}
      onPointerLeave={() => {
        if (!useDialogDisclosure && !clickOpenedRef.current) scheduleClose();
      }}
      onPointerDown={() => {
        cancelScheduledClose();
        clickOpenedRef.current = true;
        setIsOpen(true);
      }}
      onClick={() => {
        cancelScheduledClose();
        clickOpenedRef.current = true;
        setIsOpen(true);
      }}
    >
      <Info className='h-4 w-4' aria-hidden='true' />
      <span className='sr-only'>Full question wording</span>
    </Button>
  );

  if (useDialogDisclosure) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={nextOpen => {
          setIsOpen(nextOpen);
          if (!nextOpen) clickOpenedRef.current = false;
        }}
      >
        {trigger}
        <DialogContent
          className='w-[calc(100vw-1rem)] max-w-lg overflow-y-auto sm:max-w-lg'
          style={{
            maxHeight: 'calc(100dvh - 80px)',
            top: '72px',
            transform: 'translateX(-50%)',
          }}
        >
          <DialogTitle className='sr-only'>Full question wording</DialogTitle>
          <DialogDescription className='sr-only'>
            Full wording for {question.description}
          </DialogDescription>
          <div className='space-y-2 text-sm'>
            <p>{wording}</p>
            <QuestionUsageText sources={sources} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={nextOpen => {
        setIsOpen(nextOpen);
        if (!nextOpen) clickOpenedRef.current = false;
      }}
    >
      <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      <PopoverContent
        align='start'
        // Keep a flipped popover below the app header, and cap it to the space
        // Radix found on the chosen side so long wording remains scrollable.
        collisionPadding={{ top: 64, right: 8, bottom: 8, left: 8 }}
        className='max-h-[min(24rem,var(--radix-popover-content-available-height))] max-w-[calc(100vw-2rem)] overflow-y-auto text-sm'
        role='tooltip'
        onOpenAutoFocus={event => event.preventDefault()}
        onCloseAutoFocus={event => event.preventDefault()}
        onPointerEnter={cancelScheduledClose}
        onPointerLeave={() => {
          if (!clickOpenedRef.current) scheduleClose();
        }}
      >
        <div className='space-y-2'>
          <p>{wording}</p>
          <QuestionUsageText sources={sources} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function QuestionUsageText({
  sources,
}: {
  sources: readonly QuestionUsageSource[];
}) {
  const contexts = [
    ...new Set(
      orderQuestionUsageSources(sources)
        .slice(1)
        .map(source => source.contextLabel)
    ),
  ];
  if (contexts.length === 0) return null;
  return (
    <p className='text-xs text-muted-foreground'>
      Also used in: {contexts.join(', ')}
    </p>
  );
}

function questionSearchText(question: QuestionCustomizationsQuestion): string {
  return [question.qid, question.description, question.question]
    .join(' ')
    .toLowerCase();
}

const QuestionCustomizations: React.FC = () => {
  const { showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  // Per-question guidance is Roger staff only. The API already withholds the fields
  // from anyone else, so this hides a section that would be empty regardless; the
  // point is that a customer is never shown that the feature exists.
  const mayEditGuidance = hasPermission(PermissionSlug.ADMIN_SUPER_USER);
  const [questions, setQuestions] = useState<QuestionCustomizationsQuestion[]>(
    []
  );
  const [defaultQids, setDefaultQids] = useState<string[]>([]);
  const [additionQids, setAdditionQids] = useState<string[]>([]);
  const [unavailableQids, setUnavailableQids] = useState<string[]>([]);
  // An ordered list rather than a plain object: the administrator edits these in
  // place, so render order has to stay put while they type.
  const [guidance, setGuidance] = useState<GuidanceEntry[]>([]);
  const [unavailableGuidanceQids, setUnavailableGuidanceQids] = useState<
    string[]
  >([]);
  const [guidanceError, setGuidanceError] = useState(false);
  const [questionUsage, setQuestionUsage] = useState<QuestionUsage>(new Map());
  const [usageMetadataFailed, setUsageMetadataFailed] = useState(false);
  const [groupSearches, setGroupSearches] = useState<Record<string, string>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saved, setSaved] = useState(false);

  const questionsByQid = useMemo(
    () => new Map(questions.map(question => [question.qid, question])),
    [questions]
  );
  const questionLabels = useMemo(
    () => questionDisplayLabels(questions),
    [questions]
  );
  const displayLabel = (question: QuestionCustomizationsQuestion) =>
    questionLabels.get(question.qid) ?? question.description;
  const defaultQidSet = useMemo(() => new Set(defaultQids), [defaultQids]);
  const selectedQidSet = useMemo(() => new Set(additionQids), [additionQids]);
  const unavailableQidSet = useMemo(
    () => new Set(unavailableQids),
    [unavailableQids]
  );
  const guidanceQidSet = useMemo(
    () => new Set(guidance.map(entry => entry.qid)),
    [guidance]
  );
  const unavailableGuidanceQidSet = useMemo(
    () => new Set(unavailableGuidanceQids),
    [unavailableGuidanceQids]
  );
  // The cap applies to every persisted addition. Unavailable additions still
  // occupy a slot until the administrator removes them.
  const additionCount = additionQids.length;
  const guidanceCount = guidance.length;
  const questionGroups = useMemo(
    () => groupQuestionCatalog(questions, questionUsage, usageMetadataFailed),
    [questions, questionUsage, usageMetadataFailed]
  );
  const selectedQuestions = useMemo(
    () =>
      additionQids.map(
        qid =>
          questionsByQid.get(qid) ?? {
            qid,
            description: 'unavailable question',
            question: '',
            available: false,
          }
      ),
    [additionQids, questionsByQid]
  );
  const selectedGroups = useMemo(() => {
    const groupKeyByQid = new Map(
      questionGroups.flatMap(group =>
        group.questions.map(question => [question.qid, group.key] as const)
      )
    );
    const selectedByGroup = new Map<string, QuestionCustomizationsQuestion[]>();
    const ungroupedQuestions: QuestionCustomizationsQuestion[] = [];
    selectedQuestions.forEach(question => {
      const groupKey = groupKeyByQid.get(question.qid);
      if (!groupKey) {
        ungroupedQuestions.push(question);
        return;
      }
      const groupQuestions = selectedByGroup.get(groupKey) ?? [];
      groupQuestions.push(question);
      selectedByGroup.set(groupKey, groupQuestions);
    });
    const groups = questionGroups
      .map(group => ({
        ...group,
        // Keep the saved addition order within each shared heading.
        questions: selectedByGroup.get(group.key) ?? [],
      }))
      .filter(group => group.questions.length > 0);
    if (ungroupedQuestions.length > 0) {
      groups.push({
        key: 'selected-unavailable',
        label: 'Unavailable questions',
        description: 'These saved questions are no longer in the catalog.',
        questions: ungroupedQuestions,
      });
    }
    return groups;
  }, [questionGroups, selectedQuestions]);

  const applyResponse = useCallback(
    (response: QuestionCustomizationsResponse) => {
      setQuestions(response.questions);
      setDefaultQids(response.default_qids);
      setAdditionQids(response.addition_qids);
      setUnavailableQids(response.unavailable_addition_qids);
      setGuidance(
        Object.entries(response.more_context_by_qid ?? {}).map(
          ([qid, text]) => ({ qid, text })
        )
      );
      setUnavailableGuidanceQids(response.unavailable_more_context_qids ?? []);
      setGuidanceError(false);
      setSaved(false);
    },
    []
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    const [catalogResult, usageResult] = await Promise.allSettled([
      getQuestionCustomizations(),
      getQuestionUsage(),
    ]);

    if (catalogResult.status === 'rejected') {
      console.error(
        'Failed to load question customizations:',
        catalogResult.reason
      );
      setLoadFailed(true);
      showError('Unable to load question customizations. Please try again.');
    } else {
      applyResponse(catalogResult.value);
      setQuestionUsage(
        usageResult.status === 'fulfilled'
          ? usageResult.value.usage
          : new Map<string, QuestionUsageSource[]>()
      );
      setUsageMetadataFailed(
        usageResult.status === 'fulfilled'
          ? usageResult.value.hasMetadataFailure
          : true
      );
      if (usageResult.status === 'rejected') {
        console.error(
          'Failed to load question usage metadata:',
          usageResult.reason
        );
      }
    }
    setIsLoading(false);
  }, [applyResponse, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const addQuestion = (question: QuestionCustomizationsQuestion) => {
    if (
      !question.available ||
      defaultQidSet.has(question.qid) ||
      selectedQidSet.has(question.qid) ||
      additionCount >= MAX_ADDITIONS
    ) {
      return;
    }
    setSaved(false);
    setAdditionQids(current => [...current, question.qid]);
  };

  const removeQuestion = (qid: string) => {
    if (defaultQidSet.has(qid)) return;
    setSaved(false);
    setAdditionQids(current =>
      current.filter(selectedQid => selectedQid !== qid)
    );
  };

  // Guidance is its own selection, not a property of an addition: its usual
  // targets are the narrative questions, which are never required-comment
  // additions. A default question can take guidance too.
  const addGuidance = (question: QuestionCustomizationsQuestion) => {
    if (
      !question.available ||
      guidanceQidSet.has(question.qid) ||
      guidanceCount >= MAX_GUIDANCE_ENTRIES
    ) {
      return;
    }
    setSaved(false);
    setGuidance(current => [...current, { qid: question.qid, text: '' }]);
  };

  const updateGuidance = (qid: string, text: string) => {
    setSaved(false);
    setGuidanceError(false);
    setGuidance(current =>
      current.map(entry => (entry.qid === qid ? { ...entry, text } : entry))
    );
  };

  const removeGuidance = (qid: string) => {
    setSaved(false);
    setGuidanceError(false);
    setGuidance(current => current.filter(entry => entry.qid !== qid));
  };

  const save = async () => {
    // An entry with no instructions would be rejected upstream anyway, and
    // dropping it silently would look like the row vanished on its own.
    if (mayEditGuidance && guidance.some(entry => !entry.text.trim())) {
      setGuidanceError(true);
      showError(BLANK_GUIDANCE_MESSAGE);
      return;
    }
    setIsSaving(true);
    try {
      applyResponse(
        await saveQuestionCustomizations(
          additionQids,
          mayEditGuidance
            ? Object.fromEntries(guidance.map(entry => [entry.qid, entry.text]))
            : undefined
        )
      );
      setSaved(true);
      showSuccess('Question customizations saved');
    } catch (error) {
      console.error('Failed to save question customizations:', error);
      showError(
        getBusinessErrorMessage(error) ??
          'Unable to save question customizations. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Question customizations</CardTitle>
          <CardDescription>
            Add questions to your agency&apos;s RogerDocs workflows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
            Loading questions...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loadFailed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Question customizations</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <Alert variant='destructive'>
            <div className='flex items-start gap-2'>
              <AlertCircle
                className='mt-0.5 h-4 w-4 shrink-0'
                aria-hidden='true'
              />
              <AlertDescription>
                Unable to load question customizations.
              </AlertDescription>
            </div>
          </Alert>
          <Button
            type='button'
            size='sm'
            variant='outline'
            onClick={() => void load()}
          >
            <RotateCcw className='mr-2 h-4 w-4' aria-hidden='true' />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='min-w-0'>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <CardTitle>Question customizations</CardTitle>
            <CardDescription>
              Add up to 30 questions to your agency&apos;s RogerDocs workflows.
            </CardDescription>
          </div>
          <Button
            type='button'
            size='sm'
            onClick={() => void save()}
            disabled={isSaving}
            className='shrink-0'
          >
            {isSaving ? (
              <>
                <Loader2
                  className='mr-2 h-4 w-4 animate-spin'
                  aria-hidden='true'
                />
                Saving...
              </>
            ) : (
              'Save questions'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='min-w-0 space-y-5'>
        {saved && (
          <div
            className='flex items-center gap-2 text-sm text-green-700'
            role='status'
          >
            <Check className='h-4 w-4' aria-hidden='true' /> Saved
          </div>
        )}

        <section
          aria-labelledby='selected-questions-heading'
          className='space-y-2'
        >
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <h3 id='selected-questions-heading' className='font-semibold'>
              Selected questions
            </h3>
            <Badge variant='outline' className='whitespace-nowrap'>
              {additionCount} of {MAX_ADDITIONS} additions
            </Badge>
          </div>
          <p className='text-sm text-muted-foreground'>
            Selected questions appear during visits. Each one requires a
            comment.
          </p>
          {selectedGroups.length > 0 ? (
            <div className='space-y-4' data-testid='selected-question-groups'>
              {selectedGroups.map(group => (
                <section
                  key={group.key}
                  aria-labelledby={`selected-question-group-${group.key}`}
                  className='space-y-2'
                >
                  <h4
                    id={`selected-question-group-${group.key}`}
                    className='text-sm font-semibold'
                  >
                    {group.label}
                  </h4>
                  <VirtualizedQuestionList
                    listId={`selected-${group.key}`}
                    label={`Selected ${group.label}`}
                    questions={group.questions}
                    renderQuestion={question => {
                      const unavailable = unavailableQidSet.has(question.qid);
                      return (
                        <div className='flex min-w-0 items-center gap-2 rounded-md border px-2 py-1'>
                          <div className='flex min-w-0 flex-1 items-center gap-1'>
                            <QuestionWording
                              question={question}
                              sources={questionUsage.get(question.qid) ?? []}
                            />
                            <p className='min-w-0 truncate'>
                              {unavailable
                                ? 'This question is no longer available. Remove it before saving.'
                                : displayLabel(question)}
                            </p>
                          </div>
                          <Button
                            type='button'
                            size='sm'
                            variant='outline'
                            onClick={() => removeQuestion(question.qid)}
                            aria-label={`Remove question: ${displayLabel(question)}`}
                            className='shrink-0'
                          >
                            <X className='mr-2 h-4 w-4' aria-hidden='true' />{' '}
                            Remove
                          </Button>
                        </div>
                      );
                    }}
                  />
                </section>
              ))}
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              No custom questions selected yet.
            </p>
          )}
        </section>

        {mayEditGuidance && (
          <section
            aria-labelledby='question-guidance-heading'
            className='space-y-2'
          >
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <h3 id='question-guidance-heading' className='font-semibold'>
                Question guidance
              </h3>
              <Badge variant='outline' className='whitespace-nowrap'>
                {guidanceCount} of {MAX_GUIDANCE_ENTRIES} entries
              </Badge>
            </div>
            <p className='text-sm text-muted-foreground'>
              Your own instructions for how a question should be answered. They
              are added to what Roger already knows about the question, and
              apply to every visit that asks it.
            </p>
            {guidanceError && (
              <Alert variant='destructive'>
                <div className='flex items-start gap-2'>
                  <AlertCircle
                    className='mt-0.5 h-4 w-4 shrink-0'
                    aria-hidden='true'
                  />
                  <AlertDescription>{BLANK_GUIDANCE_MESSAGE}</AlertDescription>
                </div>
              </Alert>
            )}
            <div className='space-y-2'>
              {guidance.map(entry => {
                const entryQuestion = questionsByQid.get(entry.qid);
                const description = entryQuestion
                  ? displayLabel(entryQuestion)
                  : '';
                const unavailable = unavailableGuidanceQidSet.has(entry.qid);
                const label = description || 'unavailable question';
                return (
                  <div
                    key={`guidance-${entry.qid}`}
                    className='min-w-0 space-y-2 rounded-md border p-3'
                  >
                    <div className='flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
                      <div className='min-w-0'>
                        <div className='flex flex-wrap items-center gap-2'>
                          {unavailable && (
                            <Badge variant='destructive'>Unavailable</Badge>
                          )}
                        </div>
                        <p>
                          {unavailable
                            ? 'This question is no longer available. Remove it before saving.'
                            : description}
                        </p>
                      </div>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => removeGuidance(entry.qid)}
                        aria-label={`Remove guidance: ${label}`}
                        className='shrink-0 self-start'
                      >
                        <X className='mr-2 h-4 w-4' aria-hidden='true' /> Remove
                      </Button>
                    </div>
                    <Textarea
                      value={entry.text}
                      maxLength={MAX_GUIDANCE_LENGTH}
                      rows={3}
                      onChange={event =>
                        updateGuidance(entry.qid, event.target.value)
                      }
                      aria-label={`Guidance for ${label}`}
                      placeholder='For example: if the patient reports no pain, say why.'
                    />
                  </div>
                );
              })}
              {guidance.length === 0 && (
                <p className='text-sm text-muted-foreground'>
                  No question guidance added yet.
                </p>
              )}
            </div>
          </section>
        )}

        {questions.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No questions are available.
          </p>
        ) : (
          <section
            aria-labelledby='question-catalog-heading'
            className='space-y-3'
          >
            <h3 id='question-catalog-heading' className='font-semibold'>
              Question catalog
            </h3>
            <div className='space-y-4'>
              {questionGroups.map(group => {
                const searchValue = groupSearches[group.key] ?? '';
                const query = searchValue.trim().toLowerCase();
                const filteredGroupQuestions = query
                  ? group.questions.filter(question =>
                      questionSearchText(question).includes(query)
                    )
                  : group.questions;
                const searchId = `question-group-search-${group.key}`;
                return (
                  <section
                    key={group.key}
                    aria-labelledby={`question-group-${group.key}`}
                    className='space-y-2'
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <h4
                        id={`question-group-${group.key}`}
                        className='text-sm font-semibold'
                      >
                        {group.label}
                      </h4>
                      <Badge variant='outline' className='whitespace-nowrap'>
                        {group.questions.length}
                      </Badge>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      {group.description}
                    </p>
                    <div className='space-y-1'>
                      <label
                        htmlFor={searchId}
                        className='text-sm text-muted-foreground'
                      >
                        Search {group.label}
                      </label>
                      <Input
                        id={searchId}
                        inputSize='sm'
                        value={searchValue}
                        onChange={event =>
                          setGroupSearches(current => ({
                            ...current,
                            [group.key]: event.target.value,
                          }))
                        }
                        placeholder={`Search ${group.label}`}
                      />
                    </div>
                    {filteredGroupQuestions.length > 0 ? (
                      <VirtualizedQuestionList
                        listId={group.key}
                        label={`${group.label} questions`}
                        questions={filteredGroupQuestions}
                        renderQuestion={question => {
                          const isDefault = defaultQidSet.has(question.qid);
                          const isSelected = selectedQidSet.has(question.qid);
                          const atLimit = additionCount >= MAX_ADDITIONS;
                          const hasGuidance = guidanceQidSet.has(question.qid);
                          const atGuidanceLimit =
                            guidanceCount >= MAX_GUIDANCE_ENTRIES;
                          return (
                            <div className='flex min-w-0 items-center gap-2 rounded-md border px-2 py-1'>
                              <div className='flex min-w-0 flex-1 items-center gap-1'>
                                <QuestionWording
                                  question={question}
                                  sources={
                                    questionUsage.get(question.qid) ?? []
                                  }
                                />
                                <p className='min-w-0 truncate'>
                                  {displayLabel(question)}
                                </p>
                              </div>
                              <Button
                                type='button'
                                size='sm'
                                variant='outline'
                                onClick={() => addQuestion(question)}
                                disabled={
                                  !question.available ||
                                  isDefault ||
                                  isSelected ||
                                  atLimit
                                }
                                aria-label={`Add question: ${displayLabel(question)}`}
                                className='shrink-0'
                              >
                                <Plus
                                  className='mr-2 h-4 w-4'
                                  aria-hidden='true'
                                />
                                {!question.available
                                  ? 'Unavailable'
                                  : isDefault
                                    ? 'Default'
                                    : isSelected
                                      ? 'Added'
                                      : 'Add'}
                              </Button>
                              {mayEditGuidance && (
                                <Button
                                  type='button'
                                  size='sm'
                                  variant='outline'
                                  onClick={() => addGuidance(question)}
                                  disabled={
                                    !question.available ||
                                    hasGuidance ||
                                    atGuidanceLimit
                                  }
                                  aria-label={`Add guidance: ${displayLabel(question)}`}
                                  className='shrink-0'
                                >
                                  <MessageSquarePlus
                                    className='mr-2 h-4 w-4'
                                    aria-hidden='true'
                                  />
                                  {hasGuidance
                                    ? 'Guidance added'
                                    : 'Add guidance'}
                                </Button>
                              )}
                            </div>
                          );
                        }}
                      />
                    ) : (
                      <p className='text-sm text-muted-foreground'>
                        No matching questions.
                      </p>
                    )}
                  </section>
                );
              })}
              {questionGroups.length === 0 && (
                <p className='text-sm text-muted-foreground'>
                  No matching questions.
                </p>
              )}
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionCustomizations;
